import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";

// Import inhouse icons
import { ArrowLeft, ChevronDown, ChevronRight, ChevronLeft, Fulfillment, Payments, Receipt, Edit } from "@/components/icons_new";

// Import utils and helpers
import { ORDER_STATUS_STYLES, ORDER_FULFILLMENT, ORDER_PAYMENT } from "@/utils/statusStyles";
import { formatDateTime } from "@/utils/formatDateTime";
import { useOrderDetailsContext } from "../context/OrderDetailsContext";

const resolveStatusStyle = (map, status) => {
  if (!status) return map.unknown;

  const normalized = status.toLowerCase().replace(/\s+/g, "");
  const entry = Object.entries(map).find(([key]) => key.toLowerCase().replace(/\s+/g, "") === normalized);
  
  return entry ? map[entry[0]] : map.unknown;
};

export default function OrderDetailsHeader() {
  const navigate = useNavigate();
  const { order } = useOrderDetailsContext();

  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const moreMenuRef = useRef(null);

  useEffect(() => {
    if (!isMoreOpen) return undefined;

    function handleClickOutside(event) {
      if (moreMenuRef.current && !moreMenuRef.current.contains(event.target)) {
        setIsMoreOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isMoreOpen]);

  if (!order) return null;

  const orderStatusStyle = resolveStatusStyle(ORDER_STATUS_STYLES, order.orderStatus);
  const fulfillmentStyle = resolveStatusStyle(ORDER_FULFILLMENT, order.fulfillmentStatus);
  const paymentStyle = resolveStatusStyle(ORDER_PAYMENT, order.paymentStatus);

  return (
    <div className="flex items-start justify-between mb-[1em]">
      <div className="flex gap-2">
        <div className="flex items-center gap-3">
          {/* Back button */}
          <button
            className="border h-fit border-[#b7b7b7] p-[5px] rounded hover:bg-[#f3f3f3]"
            onClick={() => navigate(-1)}
            aria-label="Go back"
          >
            <ArrowLeft className="h-[1em] w-[1em] text-[#333333]" />
          </button>

          <div className="flex flex-col">
            {/* Order number */}
            <span className="text-lg font-semibold text-[#333333]">
              {order.orderNumber}
            </span>

            {/* Order created at */}
            <span className="text-[13px] text-[#4b4b4b]">
              {formatDateTime(order.createdAt)}
            </span>
          </div>
        </div>

        <div className="ml-4 flex flex-wrap gap-2">
          {/* Status of the order */}
          <span className={`h-fit flex items-center gap-[5px] rounded-full px-2 py-1 text-[0.8em] font-medium ${orderStatusStyle.color}`}>
            <Receipt className="h-[1.2em] w-[1.2em]"/>
            {orderStatusStyle.label ?? order.orderStatus}
          </span>

          {/* Payment status */}
          <span className={`h-fit flex items-center gap-[5px] rounded-full px-2 py-1 text-[0.8em] font-medium ${paymentStyle.color}`}>
            <Payments className="h-[1.2em] w-[1.2em]"/>
            {paymentStyle.label}
          </span>
          
          {/* Fulfillment status */}
          <span className={`h-fit flex items-center gap-[5px] rounded-full px-2 py-1 text-[0.8em] font-medium ${fulfillmentStyle.color}`}>
            <Fulfillment className="h-[1.2em] w-[1.2em]"/>
            {fulfillmentStyle.label}
          </span>
        </div>
      </div>

      <div className="flex items-center gap-2">
        {/* Edit order button */}
        <button className="flex items-center gap-[5px] px-[10px] py-[3px] text-sm font-medium text-[#333333] border border-[#878787] bg-none rounded-full hover:bg-[#77777724]">
          <Edit className=""/>
          Edit order
        </button>

        <div className="relative" ref={moreMenuRef}>
          {/* More action button */}
          <button
            onClick={() => setIsMoreOpen((prev) => !prev)}
            className="px-[10px] py-[3px] text-sm font-medium text-[#333333] border border-[#878787] bg-none rounded-full hover:bg-[#77777724] flex items-center gap-2"
          >
            More actions
            <ChevronDown className="h-4 w-4 text-[#333333]" />
          </button>

          {isMoreOpen && (
            <div className="absolute left-0 mt-2 w-56 rounded-lg border border-gray-200 bg-white shadow-lg z-50">
              <div className="p-1">
                {[
                  { label: "Cancel order", key: "cancel" },
                  { label: "Duplicate order", key: "duplicate" },
                  { label: "Archive order", key: "archive" },
                  { label: "Message customer", key: "message" },
                ].map((action) => (
                  <button
                    key={action.key}
                    onClick={() => setIsMoreOpen(false)}
                    className="w-full text-left px-4 py-2 rounded-md text-sm font-medium text-[#333333] hover:bg-[#a7a7a72c]"
                  >
                    {action.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="ml-[15px] flex items-center gap-[5px]">
          <button className="border border-[#b7b7b7] p-[5px] rounded hover:bg-[#f3f3f3]" aria-label="Previous order">
            <ChevronLeft className="h-[1em] w-[1em] text-[#333333]" />
          </button>
          <button className="border border-[#b7b7b7] p-[5px] rounded hover:bg-[#f3f3f3]" aria-label="Next order">
            <ChevronRight className="h-[1em] w-[1em] text-[#333333]" />
          </button>
        </div>
      </div>
    </div>
  );
}
